import { useState } from 'react';
import { QueryKey, UseInfiniteQueryResult, useQueryClient } from 'react-query';

/**
 * Custom hook to refetch the current page of an infinite query first before other pages
 * so we don't have to wait for all the other pages to be loaded just to see the current page displayed.
 * Useful for refreshing the current page data in a paginated view
 * @param infiniteQueryResult - The result of the infinite query.
 * @param currentPage - The current page number.
 * @param queryKey - The query key for the infinite query.
 * @returns An object containing the refetch function and a loading state.
 */
export const useRefetchCurrentPageFirst = <T>(
  infiniteQueryResult: UseInfiniteQueryResult<T, unknown>,
  currentPage: number,
  queryKey: QueryKey
) => {
  const queryClient = useQueryClient();
  const [refetchingPage, setRefetchingPage] = useState(0);
  const { data, refetch, isRefetching, isFetching, isFetchingNextPage, isFetchingPreviousPage } = infiniteQueryResult;

  const refetchCurrentPageFirst = async () => {
    const prevData = data;
    try {
      await refetch({
        refetchPage: (_, index) => {
          setRefetchingPage(index + 1);
          return index === currentPage - 1;
        },
      });
      await refetch({
        refetchPage: (_, index) => {
          setRefetchingPage(index + 1);
          return index !== currentPage - 1;
        },
      });
    } catch (error) {
      console.error('Error refetching current page:', error);
      // If any of the above refetch calls fail, revert to previous data
      queryClient.setQueryData(queryKey, prevData);
    } finally {
      setRefetchingPage(0);
    }
  };

  const isRefetchTriggered = isRefetching && !isFetchingNextPage && !isFetchingPreviousPage;
  const isNotRefetchingOtherPages = !isRefetchTriggered || (refetchingPage === currentPage && isRefetchTriggered);
  const isFetchingPageRelevantData = isFetching && isNotRefetchingOtherPages;

  // use isFetchingPageRelevantData instead of isFetching to determine if the current page is being fetched to show loaders
  return { refetchCurrentPageFirst, isFetchingPageRelevantData };
};
